import React, { useEffect, useState } from 'react'
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "./OrdersTable.css"
import { userRequest } from '../../RequestMethods';
import OrderDetails from '../../Pages/Orders/OrderDetails/OrderDetails';

const OrdersTable = ({ orders: propOrders, startDate, endDate, filterGroup, search }) => {
    
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        if (!propOrders || !Array.isArray(propOrders)) {
            setFilteredOrders([]);
            return;
        }

        let filtered = [...propOrders];

        // Filter by date range
        if (startDate && endDate) {
            filtered = filtered.filter(order => {
                if (!order.createdAt) return true;
                const orderDate = new Date(order.createdAt);
                const start = new Date(startDate);
                const end = new Date(endDate);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                return orderDate >= start && orderDate <= end;
            });
        }

        // Filter by status
        if (filterGroup && filterGroup !== "Tous") {
            let statusMap = {
                "Reçu": "reçu",
                "Prêt": "prêt",
                "Livré": "livré",
                "Annulé": "annulé"
            };
            const status = statusMap[filterGroup];
            if (status) {
                filtered = filtered.filter(order => order.status === status);
            }
        }

        // Filter by search term
        if (search && search.trim() !== "") {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(order => 
                order.company?.toLowerCase().includes(searchLower) ||
                order.number?.toLowerCase().includes(searchLower) ||
                order.email?.toLowerCase().includes(searchLower) ||
                order._id?.toLowerCase().includes(searchLower) ||
                order.total?.toString().toLowerCase().includes(searchLower)
            );
        }

        // Sort by creation date (newest first)
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setFilteredOrders(filtered);
    }, [propOrders, startDate, endDate, filterGroup, search]);

    const makeStyle = (status) => {
        switch(status) {
            case 'reçu':
                return { backgroundColor: '#FFE4B5', color: '#FF8C00' };
            case 'prêt':
                return { backgroundColor: '#D0F0C0', color: '#228B22' };
            case 'livré':
                return { backgroundColor: '#F3E8FF', color: '#a855f7' };
            case 'annulé':
                return { backgroundColor: '#F5C6CB', color: '#B02A37' };
            default:
                return { backgroundColor: '#E0E0E0', color: '#666666' };
        }
    };

    const updateRowField = async (id, field, value) => {
        try {
            const response = await userRequest.put(`orders/${id}`, { [field]: value });

            if (response.data) {
                // Update the local state
                setFilteredOrders(prevOrders =>
                    prevOrders.map(order =>
                        order._id === id ? { ...order, [field]: value } : order
                    )
                );
            }
        } catch (err) {
            console.error(`Error updating order ${field}:`, err);
        }
    };

    const updateRowStatus = (id, newStatus) => updateRowField(id, "status", newStatus);

    const updateRowProductionStatus = (id, newValue) =>
        updateRowField(id, "productionStatus", newValue || null);

    const handleViewDetails = (order) => {
        setSelectedOrder(order);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedOrder(null);
    };

    if (!filteredOrders || filteredOrders.length === 0) {
        return (
            <div className="Table">
                <TableContainer component={Paper} style={{ boxShadow: "0px 13px 20px 0px #80808029" }}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Commande ID</TableCell>
                                <TableCell align="left">Entreprise</TableCell>
                                <TableCell align="left">Adresse Mail</TableCell>
                                <TableCell align="left">Téléphone</TableCell>
                                <TableCell>Produits</TableCell>
                                <TableCell align="left">Total</TableCell>
                                <TableCell align="left">Status</TableCell>
                                <TableCell align="left">Production</TableCell>
                                <TableCell align="left">Date</TableCell>
                                <TableCell align="left"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell colSpan={10} align="center" style={{ padding: "50px" }}>
                                    Aucune commande trouvée
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );
    }

    return (
        <>
            <div className="Table">
                <TableContainer component={Paper} style={{ boxShadow: "0px 13px 20px 0px #80808029", overflowY: "auto" }}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Commande ID</TableCell>
                                <TableCell align="left">Entreprise</TableCell>
                                <TableCell align="left">Adresse Mail</TableCell>
                                <TableCell align="left">Téléphone</TableCell>
                                <TableCell>Produits</TableCell>
                                <TableCell align="left">Total</TableCell>
                                <TableCell align="left">Status</TableCell>
                                <TableCell align="left">Production</TableCell>
                                <TableCell align="left">Date</TableCell>
                                <TableCell align="left"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredOrders.map((order, index) => (
                                <TableRow key={order._id || index} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                    <TableCell align="left">{order._id?.slice(-8) || 'N/A'}</TableCell>
                                    <TableCell align="left">{order.company || 'N/A'}</TableCell>
                                    <TableCell align="left">{order.email || 'N/A'}</TableCell>
                                    <TableCell align="left">{order.number || 'N/A'}</TableCell>
                                    <TableCell component="th" scope="row">
                                        {order.products?.length || 0} produit(s)
                                    </TableCell>
                                    <TableCell align="left">{order.total} €</TableCell>
                                    <TableCell align="left">
                                        <select
                                            className="status"
                                            style={makeStyle(order.status)}
                                            value={order.status}
                                            onChange={(e) => updateRowStatus(order._id, e.target.value)}
                                        >
                                            <option value="reçu">Reçu</option>
                                            <option value="prêt">Prêt</option>
                                            <option value="livré">Livré</option>
                                            <option value="annulé">Annulé</option>
                                        </select>
                                    </TableCell>
                                    <TableCell align="left">
                                        <div className="production-cell">
                                            <select
                                                className="status"
                                                style={order.productionStatus === 'en_cours' ? { backgroundColor: '#dbeafe', color: '#1e40af' } : { backgroundColor: '#E0E0E0', color: '#666666' }}
                                                value={order.productionStatus || ''}
                                                onChange={(e) => updateRowProductionStatus(order._id, e.target.value)}
                                            >
                                                <option value="">Aucun</option>
                                                <option value="en_cours">En cours</option>
                                            </select>
                                            {order.stockShortfall?.length > 0 && (
                                                <span
                                                    className="shortfall-badge"
                                                    title={`Rupture de stock sur ${order.stockShortfall.length} ligne(s)`}
                                                >
                                                    Rupture de stock
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell align="left">
                                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell align="left">
                                        <button 
                                            className="Details"
                                            onClick={() => handleViewDetails(order)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#0B2A5C',
                                                fontSize: '14px'
                                            }}
                                        >
                                            Détails
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            
            {/* Order Details Modal */}
            <OrderDetails
                open={modalOpen}
                onClose={handleCloseModal}
                orderData={selectedOrder}
            />
        </>
    );
};

export default OrdersTable;